import React, {
  ComponentType,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { Dayjs } from "dayjs";

import KpiCardJs from "../../shared/Dashboard/KpiCard/KpiCard";
import DatePickerJs from "../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import SportsbookIcon from "../../../assets/icons/ic_sportsbook.svg";
import CasinoIcon from "../../../assets/icons/ic_casino.svg";
import FinancialIcon from "../../../assets/icons/ic_payments.svg";
import AcquisitionIcon from "../../../assets/icons/ic_line-chart.svg";
import ProductPerformanceIcon from "../../../assets/icons/ic_target.svg";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getAdminNow } from "../../../utils/dateService";
import { useGetFinancialsKpiCards } from "../../../queries/financials/useGetFinancialsKpiCards";
import { useGetAcquisitionKpiCards } from "../../../queries/acquisition/useGetAcquisitionKpiCards";
import { useGetSportsbookKpiCards } from "../../../queries/sportsbook-kpis/useGetSportsbookKpiCards";
import { useGetCasinoOverviewKpiCards } from "../../../queries/casino-kpis/useGetCasinoOverviewKpiCards";
import {
  KPI_PAGE_PERMISSIONS,
  SHOW_ALL_KPI_SECTIONS,
} from "../../../utils/Constants";
import AppContext from "../../../context/AppContext";
import { useKpiTabToolbar } from "../KpiTabToolbarContext";
import {
  calculatePercentChange as calculatePercentChangeShared,
  computeRedeemabilityCard,
  getRedeemabilityPctTitle,
} from "../../../utils/kpiMath";
import { useIsSweepstakesApplication } from "../../../hooks/useIsSweepstakesApplication";

import styles from "./OverviewV2.module.css";
import { RightOutlined } from "@ant-design/icons";

const KpiCard = KpiCardJs as ComponentType<any>;
const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

interface KpiItem {
  title: string;
  value: number;
  percentage: number;
  rate?: boolean;
  maximumFractionDigits?: number;
  titleTooltip?: string;
  hidePercentage?: boolean;
}

interface OverviewV2Props {
  onNavigateToTab?: (tab: string) => void;
  currencySymbol?: string;
}

const SEE_ALL_TAB_BY_SECTION: Record<string, string> = {
  [AppStrings.FINANCIALS]: "financials-v2",
  [AppStrings.ACQUISITION]: "acquisition",
  [AppStrings.SPORTSBOOK]: "sportsbook-v2",
  [AppStrings.CASINO]: "casino",
};

interface KpiSectionProps {
  icon: string;
  title: string;
  kpis: KpiItem[];
  currencySymbol?: string;
  isLoading?: boolean;
  isError?: boolean;
  columns?: number;
  onSeeAll?: () => void;
  showSeeAll?: boolean;
}

interface KpiExportRow {
  section: string;
  metric: string;
  value: number;
  change_pct: number;
}

const buildExportRows = (
  sections: { title: string; kpis: KpiItem[] }[],
): KpiExportRow[] =>
  sections.flatMap(({ title, kpis }) =>
    kpis.map((kpi) => ({
      section: title,
      metric: kpi.title,
      value: kpi.value,
      change_pct: kpi.percentage,
    })),
  );

const FINANCIALS_META: Record<
  string,
  {
    titleKey: keyof typeof AppStrings;
    titleTooltipKey?: keyof typeof AppStrings;
    rate?: boolean;
    maximumFractionDigits?: number;
  }
> = {
  deposit_amount: { titleKey: "OVERVIEW_DEPOSIT_AMOUNT" },
  withdrawal_amount: { titleKey: "OVERVIEW_WITHDRAWAL_AMOUNT" },
  revenue: { titleKey: "OVERVIEW_REVENUE" },
  profitability_pct: { titleKey: "OVERVIEW_PROFITABILITY_PCT", rate: true },
  // Frontend-derived from withdrawal_amount / deposit_amount * 100. Lives
  // in this section because admins reason about deposit-to-withdrawal
  // efficiency alongside the other cash-flow metrics, even though the BE
  // doesn't ship it on the financials response.
  redeemability_pct: { titleKey: "OVERVIEW_REDEEMABILITY_PCT", rate: true },
  total_cash_balance: {
    titleKey: "OVERVIEW_TOTAL_CASH_BALANCE",
    titleTooltipKey: "OVERVIEW_TOTAL_CASH_BALANCE_TOOLTIP",
  },
  non_wagered_cash: {
    titleKey: "OVERVIEW_NON_WAGERED_CASH",
    titleTooltipKey: "OVERVIEW_NON_WAGERED_CASH_TOOLTIP",
  },
  redeemable_cash: {
    titleKey: "OVERVIEW_REDEEMABLE_CASH",
    titleTooltipKey: "OVERVIEW_REDEEMABLE_CASH_TOOLTIP",
  },
};

const FINANCIALS_ORDER = [
  "deposit_amount", "withdrawal_amount", "revenue", "profitability_pct",
  "total_cash_balance", "non_wagered_cash", "redeemable_cash", "redeemability_pct",
];

const SPORTSBOOK_META: Record<string, { titleKey: keyof typeof AppStrings; rate?: boolean; maximumFractionDigits?: number }> = {
  turnover: { titleKey: "TURNOVER" },
  pnl: { titleKey: "PROFIT_LOSS" },
  margin_pct: { titleKey: "MARGIN_PCT", rate: true },
  total_bets: { titleKey: "TOTAL_BETS", maximumFractionDigits: 0 },
  avg_bet_amount: { titleKey: "AVG_BET_AMOUNT" },
  total_users: { titleKey: "TOTAL_ACTIVE_USERS", maximumFractionDigits: 0 },
};

const SPORTSBOOK_ORDER = ["turnover", "pnl", "margin_pct", "total_bets", "avg_bet_amount", "total_users"];

const ACQUISITION_META: Record<
  string,
  {
    titleKey: keyof typeof AppStrings;
    titleTooltipKey?: keyof typeof AppStrings;
    hidePercentage?: boolean;
    rate?: boolean;
    maximumFractionDigits?: number;
  }
> = {
  new_users: { titleKey: "OVERVIEW_NEW_USERS", maximumFractionDigits: 0 },
  ftd_count: { titleKey: "OVERVIEW_FTD_COUNT", maximumFractionDigits: 0 },
  ftd_amount: { titleKey: "OVERVIEW_FTD_AMOUNT" },
  avg_ftd_amount: { titleKey: "OVERVIEW_AVG_FTD_AMOUNT" },
  total_users: {
    titleKey: "OVERVIEW_TOTAL_USERS",
    maximumFractionDigits: 0,
    titleTooltipKey: "TOTAL_USERS_ALL_TIME_TOOLTIP",
    hidePercentage: true,
  },
  // Sourced from the financials cards response, not user-analytics —
  // surfaced under Acquisition because admins read it as "how many of
  // my acquired users are funded depositors".
  active_deposit_users: {
    titleKey: "OVERVIEW_ACTIVE_DEPOSIT_USERS",
    maximumFractionDigits: 0,
  },
};

const ACQUISITION_ORDER = [
  "total_users",
  "new_users",
  "ftd_count",
  "ftd_amount",
  "avg_ftd_amount",
  "active_deposit_users",
];

const CASINO_META: Record<string, { titleKey: keyof typeof AppStrings; rate?: boolean; maximumFractionDigits?: number }> = {
  turnover: { titleKey: "TURNOVER" },
  ggr: { titleKey: "OVERVIEW_GGR" },
  margin_pct: { titleKey: "MARGIN_PCT", rate: true },
  total_rounds: { titleKey: "OVERVIEW_TOTAL_ROUNDS", maximumFractionDigits: 0 },
  avg_bet_amount: { titleKey: "AVG_BET_AMOUNT" },
  total_users: { titleKey: "TOTAL_ACTIVE_USERS", maximumFractionDigits: 0 },
};

const CASINO_ORDER = ["turnover", "ggr", "margin_pct", "total_rounds", "avg_bet_amount", "total_users"];


/**
 * Re-exported from `utils/kpiMath` to preserve the existing test import
 * path (`import { calculatePercentChange } from "./OverviewV2"`).
 * Production code should pull from `utils/kpiMath` directly.
 */
export const calculatePercentChange = calculatePercentChangeShared;

interface KpiSectionConfig {
  permission: string | string[];
  icon: string;
  title: string;
  kpis: KpiItem[];
  columns?: number;
  showSeeAll?: boolean;
  isLoading?: boolean;
  isError?: boolean;
}

// Section is visible when the role has every required permission. Sections
// that combine data from multiple products (e.g. Product Performance pulls
// sportsbook + casino) ship with an array so a single-product role can't see
// the combined numbers.
const sectionHasAccess = (
  permission: string | string[],
  adminPermissions: string[],
): boolean => {
  if (Array.isArray(permission)) {
    return permission.every((p) => adminPermissions.includes(p));
  }
  return adminPermissions.includes(permission);
};

const KpiSection: React.FC<KpiSectionProps> = ({
  icon,
  title,
  kpis,
  currencySymbol,
  isLoading = false,
  isError = false,
  columns = 3,
  onSeeAll,
  showSeeAll = true,
}) => (
  <section className={styles.card} aria-label={`${title} overview`}>
    <header className={styles.cardHeader}>
      <div className={styles.cardTitleGroup}>
        <img className={styles.cardIcon} src={icon} alt="" aria-hidden="true" />
        <h2 className={styles.cardTitle}>{title}</h2>
      </div>
      {showSeeAll && (
        <button type="button" className={styles.seeAll} onClick={onSeeAll}>
          {AppStrings.SEE_ALL}
          <span className={styles.seeAllChevron} aria-hidden="true">
            <RightOutlined />
          </span>
        </button>
      )}
    </header>

    {isError && (
      <div className={styles.errorBanner} data-testid="kpi-section-error">
        {AppStrings.SPORTSBOOK_KPI_CARDS_ERROR}
      </div>
    )}

    <div
      className={styles.kpiGrid}
      style={{ "--columns": columns } as React.CSSProperties}
      data-testid="kpi-grid"
    >
      {kpis.map((item) => (
        <KpiCard
          key={item.title}
          isApiRunning={isLoading}
          title={item.title}
          titleTooltip={item.titleTooltip}
          value={item.value}
          percentage={item.percentage}
          hidePercentage={item.hidePercentage ?? false}
          rate={item.rate ?? false}
          maximumFractionDigits={item.maximumFractionDigits ?? 2}
          currencySymbol={currencySymbol}
          hugeValue={false}
          backgroundColor="var(--grey-750)"
        />
      ))}
    </div>
  </section>
);

const mapKpiPlaceholder = (
  order: string[],
  meta: Record<
    string,
    {
      titleKey: keyof typeof AppStrings;
      titleTooltipKey?: keyof typeof AppStrings;
      hidePercentage?: boolean;
      rate?: boolean;
      maximumFractionDigits?: number;
    }
  >,
): KpiItem[] =>
  order.map((key) => {
    const fieldMeta = meta[key];
    return {
      title: AppStrings[fieldMeta.titleKey],
      value: 0,
      percentage: 0,
      rate: fieldMeta.rate,
      maximumFractionDigits: fieldMeta.maximumFractionDigits,
      titleTooltip: fieldMeta.titleTooltipKey
        ? AppStrings[fieldMeta.titleTooltipKey]
        : undefined,
      hidePercentage: fieldMeta.hidePercentage,
    };
  });

const mapKpiFromMeta = (
  order: string[],
  meta: Record<
    string,
    {
      titleKey: keyof typeof AppStrings;
      titleTooltipKey?: keyof typeof AppStrings;
      hidePercentage?: boolean;
      rate?: boolean;
      maximumFractionDigits?: number;
    }
  >,
  getEntry: (key: string) =>
    | {
        value?: number | null;
        percent_change?: number | null;
        percentage?: number | null;
      }
    | undefined,
): KpiItem[] =>
  order.map((key) => {
    const entry = getEntry(key);
    const fieldMeta = meta[key];
    return {
      title: AppStrings[fieldMeta.titleKey],
      value: entry?.value ?? 0,
      percentage: entry?.percent_change ?? entry?.percentage ?? 0,
      rate: fieldMeta.rate,
      maximumFractionDigits: fieldMeta.maximumFractionDigits,
      titleTooltip: fieldMeta.titleTooltipKey
        ? AppStrings[fieldMeta.titleTooltipKey]
        : undefined,
      hidePercentage: fieldMeta.hidePercentage,
    };
  });

const OverviewV2: React.FC<OverviewV2Props> = ({
  onNavigateToTab,
  currencySymbol,
}) => {
  const { adminPermissions } = useContext(AppContext);
  const isSweepstakes = useIsSweepstakesApplication();
  const redeemabilityTitle = getRedeemabilityPctTitle(isSweepstakes);

  const [dates, setDates] = useState<DateRange>(() => {
    const now = getAdminNow();
    return [now.startOf("month"), now.endOf("day")];
  });

  const dateFilters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    }),
    [dates],
  );

  const { data: financialsRaw, isFetching: financialsLoading, isError: financialsError } =
    useGetFinancialsKpiCards(dateFilters);

  const { data: acquisitionRaw, isFetching: acquisitionLoading, isError: acquisitionError } =
    useGetAcquisitionKpiCards(dateFilters);

  const { data: sportsbookResult, isFetching: sportsbookLoading, isError: sportsbookError } =
    useGetSportsbookKpiCards(dateFilters);
  const sportsbookCards = useMemo(
    () => sportsbookResult?.cards ?? [],
    [sportsbookResult?.cards],
  );

  const { data: casinoRaw, isFetching: casinoLoading, isError: casinoError } =
    useGetCasinoOverviewKpiCards(dateFilters);

  const financialKpis: KpiItem[] = useMemo(() => {
    const withRedeemabilityTitle = (items: KpiItem[]) =>
      items.map((item, index) =>
        FINANCIALS_ORDER[index] === "redeemability_pct"
          ? { ...item, title: redeemabilityTitle }
          : item,
      );

    if (!financialsRaw) {
      return withRedeemabilityTitle(
        mapKpiPlaceholder(FINANCIALS_ORDER, FINANCIALS_META),
      );
    }
    // Frontend-derived `redeemability_pct` lives in `utils/kpiMath` so
    // the formula is shared with `FinancialsV2` (no copy-paste drift).
    const redeemabilityCard = computeRedeemabilityCard(financialsRaw);

    return withRedeemabilityTitle(
      mapKpiFromMeta(FINANCIALS_ORDER, FINANCIALS_META, (key) => {
        if (key === "redeemability_pct") return redeemabilityCard;
        return financialsRaw[key as keyof typeof financialsRaw];
      }),
    );
  }, [financialsRaw, redeemabilityTitle]);

  const acquisitionKpis: KpiItem[] = useMemo(() => {
    if (!acquisitionRaw) {
      return mapKpiPlaceholder(ACQUISITION_ORDER, ACQUISITION_META);
    }
    // `active_deposit_users` lives on the financials response, not on the
    // acquisition response — pulled in here so the on-screen Acquisition
    // section can surface "how many users are funded depositors" without
    // requiring a BE schema change.
    return mapKpiFromMeta(ACQUISITION_ORDER, ACQUISITION_META, (key) => {
      if (key === "active_deposit_users") {
        return financialsRaw?.active_deposit_users;
      }
      return acquisitionRaw[key as keyof typeof acquisitionRaw];
    });
  }, [acquisitionRaw, financialsRaw]);

  const sportsbookKpis: KpiItem[] = useMemo(() => {
    if (sportsbookCards.length === 0) {
      return mapKpiPlaceholder(SPORTSBOOK_ORDER, SPORTSBOOK_META);
    }
    const cardsByKey = new Map(sportsbookCards.map((c) => [c.key, c]));
    return mapKpiFromMeta(SPORTSBOOK_ORDER, SPORTSBOOK_META, (key) => {
      const card = cardsByKey.get(key);
      if (!card) return undefined;
      return { value: card.value, percentage: card.percentage };
    });
  }, [sportsbookCards]);

  const casinoKpis: KpiItem[] = useMemo(() => {
    if (!casinoRaw) {
      return mapKpiPlaceholder(CASINO_ORDER, CASINO_META);
    }
    return mapKpiFromMeta(
      CASINO_ORDER,
      CASINO_META,
      (key) => casinoRaw[key as keyof typeof casinoRaw],
    );
  }, [casinoRaw]);

  const productPerformanceKpis: KpiItem[] = useMemo(() => {
    const sportsbookCardsByKey = new Map(sportsbookCards.map((card) => [card.key, card]));
    const sportsbookTurnover = sportsbookCardsByKey.get("turnover");
    const sportsbookPnl = sportsbookCardsByKey.get("pnl");

    const turnover = (sportsbookTurnover?.value ?? 0) + (casinoRaw?.turnover?.value ?? 0);
    const previousTurnover =
      (sportsbookTurnover?.previousValue ?? 0) + (casinoRaw?.turnover?.previous_value ?? 0);
    const profitAndLoss = (sportsbookPnl?.value ?? 0) + (casinoRaw?.ggr?.value ?? 0);
    const previousProfitAndLoss =
      (sportsbookPnl?.previousValue ?? 0) + (casinoRaw?.ggr?.previous_value ?? 0);
    const marginPct = turnover === 0 ? 0 : (profitAndLoss / turnover) * 100;
    const previousMarginPct =
      previousTurnover === 0 ? 0 : (previousProfitAndLoss / previousTurnover) * 100;

    return [
      {
        title: AppStrings.TURNOVER,
        value: turnover,
        percentage: calculatePercentChange(turnover, previousTurnover),
      },
      {
        title: AppStrings.PROFIT_LOSS,
        value: profitAndLoss,
        percentage: calculatePercentChange(profitAndLoss, previousProfitAndLoss),
      },
      {
        title: AppStrings.MARGIN_PCT,
        value: marginPct,
        percentage: calculatePercentChange(marginPct, previousMarginPct),
        rate: true,
      },
    ];
  }, [casinoRaw, sportsbookCards]);

  const getData = useCallback((_queryString: string | null) => {}, []);

  const handleSeeAll = useCallback(
    (sectionTitle: string) => {
      const tab = SEE_ALL_TAB_BY_SECTION[sectionTitle];
      if (tab) {
        onNavigateToTab?.(tab);
      }
    },
    [onNavigateToTab],
  );

  const sections: KpiSectionConfig[] = useMemo(
    () => [
      {
        permission: KPI_PAGE_PERMISSIONS.FINANCIALS,
        icon: FinancialIcon,
        title: AppStrings.FINANCIALS,
        kpis: financialKpis,
        columns: 4,
        isLoading: financialsLoading,
        isError: financialsError,
      },
      {
        permission: KPI_PAGE_PERMISSIONS.ACQUISITION,
        icon: AcquisitionIcon,
        title: AppStrings.ACQUISITION,
        kpis: acquisitionKpis,
        isLoading: acquisitionLoading,
        isError: acquisitionError,
      },
      {
        permission: [KPI_PAGE_PERMISSIONS.SPORTSBOOK, KPI_PAGE_PERMISSIONS.CASINO],
        icon: ProductPerformanceIcon,
        title: AppStrings.PRODUCT_PERFORMANCE,
        kpis: productPerformanceKpis,
        isLoading: sportsbookLoading || casinoLoading,
        isError: sportsbookError || casinoError,
        showSeeAll: false,
      },
      {
        permission: KPI_PAGE_PERMISSIONS.SPORTSBOOK,
        icon: SportsbookIcon,
        title: AppStrings.SPORTSBOOK,
        kpis: sportsbookKpis,
        isLoading: sportsbookLoading,
        isError: sportsbookError,
      },
      {
        permission: KPI_PAGE_PERMISSIONS.CASINO,
        icon: CasinoIcon,
        title: AppStrings.CASINO,
        kpis: casinoKpis,
        isLoading: casinoLoading,
        isError: casinoError,
      },
    ],
    [
      financialKpis,
      acquisitionKpis,
      productPerformanceKpis,
      sportsbookKpis,
      casinoKpis,
      financialsLoading,
      financialsError,
      acquisitionLoading,
      acquisitionError,
      sportsbookLoading,
      sportsbookError,
      casinoLoading,
      casinoError,
    ],
  );

  // Only the sections the role is allowed to see. Hidden sections also drop
  // out of the export so the spreadsheet matches what's on screen.
  const visibleSections = useMemo(
    () =>
      sections.filter(
        (section) =>
          SHOW_ALL_KPI_SECTIONS ||
          (Array.isArray(adminPermissions) &&
            sectionHasAccess(section.permission, adminPermissions)),
      ),
    [sections, adminPermissions],
  );

  const exportRows = useMemo(
    () => buildExportRows(visibleSections),
    [visibleSections],
  );

  // Portal the date picker + export button into the shared KPI tabs row
  // so they live next to the tab strip. Avoids the overflow-into-tabs bug
  // an absolute-positioned header used to cause on narrow viewports
  // (e.g. iPhone landscape) — the shared row already wraps gracefully.
  const tabToolbar = useMemo(
    () => (
      <>
        <CustomDatePicker
          getData={getData}
          dates={dates}
          setDates={setDates}
          showPresets={true}
        />
        <ExportToXlsxButton data={exportRows} filename="kpi-overview.xlsx" />
      </>
    ),
    [dates, exportRows, getData],
  );

  useKpiTabToolbar(tabToolbar);

  return (
    <div className={styles.overviewV2}>
      {visibleSections.map((section) => (
        <KpiSection
          key={section.title}
          icon={section.icon}
          title={section.title}
          kpis={section.kpis}
          currencySymbol={currencySymbol}
          columns={section.columns}
          showSeeAll={section.showSeeAll}
          isLoading={section.isLoading}
          isError={section.isError}
          onSeeAll={() => handleSeeAll(section.title)}
        />
      ))}
    </div>
  );
};

export default OverviewV2;

